import { Injectable } from '@angular/core';
import {
  Analytics,
  getAnalytics,
  logEvent as logWebEvent,
  setAnalyticsCollectionEnabled,
} from '@angular/fire/analytics';
import { initializeApp } from '@angular/fire/app';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import { TrackingService } from '../../../services/tracking.service';

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private readonly analytics: Analytics | undefined;
  private readonly platform = Capacitor.getPlatform();
  private eventQueue: Array<{ name: string; params?: Record<string, any> }> = [];
  private sendingQueue = false;

  constructor(private trackingService: TrackingService) {
    if (this.platform === 'web') {
      const app = initializeApp(environment.firebase);
      this.analytics = getAnalytics(app);
      setAnalyticsCollectionEnabled(this.analytics, this.trackingService.isAnalyticsAllowed());
    } else {
      FirebaseAnalytics.setEnabled({ enabled: this.trackingService.isAnalyticsAllowed() });
    }
  }

  async setEnableAnalytics(consent: boolean) {
    this.trackingService.setAnalyticsConsent(consent ? 'true' : 'false');

    if (this.platform === 'web' && this.analytics) {
      setAnalyticsCollectionEnabled(this.analytics, consent);
    } else {
      await FirebaseAnalytics.setEnabled({ enabled: consent });
    }

    if (consent) {
      await this.flushQueue();
    }
  }

  async logEvent(name: string, params?: Record<string, any>) {
    const consent = this.trackingService.isAnalyticsAllowed();

    if (!consent) {
      // Queue the event if consent is not yet granted
      this.eventQueue.push({ name, params });
      return;
    }

    await this.sendEvent(name, params);
  }

  private async sendEvent(name: string, params?: Record<string, any>) {
    if (this.platform === 'web' && this.analytics) {
      logWebEvent(this.analytics, name, params);
    } else {
      await FirebaseAnalytics.logEvent({ name, params });
    }
  }

  private async flushQueue() {
    if (this.sendingQueue || this.eventQueue.length === 0) return;

    this.sendingQueue = true;

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()!;
      await this.sendEvent(event.name, event.params);
    }

    this.sendingQueue = false;
  }
}
